/**
 * web/v2/src/widgets/DmHoneynet.jsx — Sovereign Discord DM Honeynet & Monitor.
 *
 * Features:
 *   - Google Chrome-style tab strip sorted by most recent DM activity
 *   - Circular Discord avatar on each tab + glowing unread/new-message aura
 *   - Authentic Discord dark-mode chat interface (#313338 / #2b2d31) with
 *     Discord markdown formatting, role/bot badges, and timestamps
 *   - Per-message Fissal Suspicion Score & Threat Reason badges
 *   - Expandable Conversation Context on filtered/searched messages: shows
 *     messages before & after (including Fissal's reply) with full interactive
 *     Flag / Edit / Delete controls on every context message
 *   - Owner Review actions: Confirm Suspicion, Dismiss (Safe), Flag AI Leak
 *   - Live Discord Message Controls: Edit Fissal's response in-place (mirrors on
 *     Discord; Fissal claims it "rethought its response for accuracy") or Delete
 *     Fissal's message on Discord (Fissal claims "automated privacy auto-pruning")
 */

import { createSignal, createMemo, createEffect, For, Show } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import toast from '../lib/toastStore.js';
import './DmHoneynet.css';

async function apiJson(url, opts = {}) {
  const res = await fetch(url, {
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
    ...opts,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

/**
 * Safe Discord Markdown formatter -> HTML string, then inserted via
 * native Sanitizer API (`Element.prototype.setHTML`) with fallback.
 */
function formatDiscordMarkdownToSafeHtml(rawText) {
  if (!rawText) return '';
  let text = String(rawText)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Fenced code blocks ```lang\ncode```
  text = text.replace(/```([a-zA-Z0-9_-]*)\n?([\s\S]*?)```/g, (_m, _lang, code) => {
    return `<pre class="dmh-codeblock"><code>${code.trim()}</code></pre>`;
  });

  // Inline code `code`
  text = text.replace(/`([^`\n]+)`/g, '<code class="dmh-inline-code">$1</code>');

  // Subtext -# line
  text = text.replace(/^-#\s+(.+)$/gm, '<div class="dmh-subtext">$1</div>');

  // Blockquote > line
  text = text.replace(/^&gt;\s?(.+)$/gm, '<blockquote class="dmh-quote">$1</blockquote>');

  // Bold + Italic ***text***
  text = text.replace(/\*\*\*([^*\n]+)\*\*\*/g, '<strong><em>$1</em></strong>');

  // Bold **text**
  text = text.replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');

  // Italic *text* or _text_
  text = text.replace(/\*([^*\n]+)\*/g, '<em>$1</em>');
  text = text.replace(/\b_([^_\n]+)_\b/g, '<em>$1</em>');

  // Strikethrough ~~text~~
  text = text.replace(/~~([^~\n]+)~~/g, '<del>$1</del>');

  // Spoilers ||text||
  text = text.replace(/\|\|([^|\n]+)\|\|/g, '<span class="dmh-spoiler" title="Spoiler">$1</span>');

  // Convert remaining newlines to <br> outside <pre>
  const parts = text.split(/(<pre class="dmh-codeblock">[\s\S]*?<\/pre>)/g);
  return parts
    .map(p => (p.startsWith('<pre ') ? p : p.replace(/\n/g, '<br>')))
    .join('');
}

function renderSanitizedDiscordHtml(el, rawText) {
  if (!el) return;
  const html = formatDiscordMarkdownToSafeHtml(rawText);
  if (typeof el.setHTML === 'function') {
    el.setHTML(html);
  } else {
    el.innerHTML = html;
  }
}

function formatDiscordTimestamp(ts) {
  if (!ts) return '';
  const d = new Date(Number(ts));
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const timeStr = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (isToday) return `Today at ${timeStr}`;
  const dateStr = d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
  return `${dateStr} ${timeStr}`;
}

function formatRelativeAgo(ts) {
  if (!ts) return '';
  const diffSec = Math.max(1, Math.floor((Date.now() - Number(ts)) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  return `${Math.floor(diffSec / 86400)}d ago`;
}

export default function DmHoneynet() {
  const [selectedUserId, setSelectedUserId] = createSignal(null);
  const [filterMode, setFilterMode] = createSignal('all'); // 'all' | 'suspicious' | 'confirmed' | 'edited_deleted'
  const [searchQuery, setSearchQuery] = createSignal('');
  const [threadData, setThreadData] = createSignal(null);
  const [threadLoading, setThreadLoading] = createSignal(false);
  const [syncing, setSyncing] = createSignal(false);

  // Edit / Flag modal states
  const [editingMsgId, setEditingMsgId] = createSignal(null);
  const [editDraft, setEditDraft] = createSignal('');
  const [flaggingMsgId, setFlaggingMsgId] = createSignal(null);
  const [flagNoteDraft, setFlagNoteDraft] = createSignal('');
  const [busyMsgId, setBusyMsgId] = createSignal(null);

  // Expandable surrounding context states (for filtered / searched views)
  const [expandedMap, setExpandedMap] = createSignal({});
  const [customContextMap, setCustomContextMap] = createSignal({});
  const [contextLoadingId, setContextLoadingId] = createSignal(null);

  let chatScrollRef = null;

  const widgetParams = createMemo(() => ({
    userId: selectedUserId() || undefined,
    filter: filterMode(),
  }));

  const { data, loading, refetch } = useWidgetData('dm-honeynet', widgetParams);

  const tabs = createMemo(() => data()?.tabs || []);

  const activeUserId = createMemo(() => {
    return selectedUserId() || data()?.activeUserId || tabs()[0]?.userId || null;
  });

  const activeTabInfo = createMemo(() => {
    const uid = activeUserId();
    return tabs().find(t => t.userId === uid) || threadData()?.user || data()?.thread?.user || null;
  });

  const isFilteredView = createMemo(() => {
    return filterMode() !== 'all' || searchQuery().trim().length > 0;
  });

  // Keep threadData synchronized with live SSE refetches from useWidgetData
  let lastSeenTailMsgId = null;
  createEffect(() => {
    const payload = data();
    const incomingThread = payload?.thread;
    const uid = activeUserId();
    if (!incomingThread || !uid || incomingThread.user?.userId !== uid) return;

    const newMsgs = incomingThread.messages || [];
    const latestId = newMsgs[newMsgs.length - 1]?.messageId || null;
    const isNewTail = latestId && latestId !== lastSeenTailMsgId;
    lastSeenTailMsgId = latestId;

    setThreadData(incomingThread);

    if (isNewTail && filterMode() === 'all' && !searchQuery().trim()) {
      setTimeout(() => {
        if (chatScrollRef) chatScrollRef.scrollTop = chatScrollRef.scrollHeight;
      }, 40);
    }
  });

  const messages = createMemo(() => {
    const localThread = threadData();
    const baseMsgs = (localThread && localThread.user?.userId === activeUserId())
      ? localThread.messages
      : (data()?.thread?.messages || []);
    const q = searchQuery().trim().toLowerCase();
    if (!q) return baseMsgs;
    return baseMsgs.filter(m =>
      (m.content || '').toLowerCase().includes(q) ||
      (m.suspicionReasons || []).some(r => r.toLowerCase().includes(q))
    );
  });

  function isContextExpanded(messageId) {
    return Boolean(expandedMap()[messageId]);
  }

  async function toggleContextExpand(msg) {
    const mid = msg.messageId;
    const currentlyExpanded = Boolean(expandedMap()[mid]);
    setExpandedMap(prev => ({ ...prev, [mid]: !currentlyExpanded }));

    const hasInlineCtx = ((msg.contextBefore?.length || 0) + (msg.contextAfter?.length || 0)) > 0;
    if (!currentlyExpanded && !hasInlineCtx && !customContextMap()[mid]) {
      await fetchMoreContext(mid, 4, 4);
    }
  }

  async function fetchMoreContext(messageId, beforeCount = 6, afterCount = 6) {
    setContextLoadingId(messageId);
    try {
      const res = await apiJson(
        `/api/v2/dm-honeynet/message/${encodeURIComponent(messageId)}/context?before=${beforeCount}&after=${afterCount}`
      );
      setCustomContextMap(prev => ({
        ...prev,
        [messageId]: {
          before: res.contextBefore || [],
          after: res.contextAfter || [],
          beforeCount,
          afterCount,
        },
      }));
    } catch (err) {
      toast.error('Failed to load surrounding context', { detail: err.message });
    } finally {
      setContextLoadingId(null);
    }
  }

  async function expandAllContexts() {
    const list = messages();
    const next = {};
    for (const m of list) {
      next[m.messageId] = true;
    }
    setExpandedMap(next);

    for (const m of list) {
      const hasInlineCtx = ((m.contextBefore?.length || 0) + (m.contextAfter?.length || 0)) > 0;
      if (!hasInlineCtx && !customContextMap()[m.messageId]) {
        fetchMoreContext(m.messageId, 4, 4);
      }
    }
  }

  function collapseAllContexts() {
    setExpandedMap({});
  }

  // Load thread whenever activeUserId or filterMode changes
  async function loadThread(uid, filter = filterMode(), autoScroll = true) {
    if (!uid) return;
    setThreadLoading(true);
    try {
      const res = await apiJson(`/api/v2/dm-honeynet/thread/${encodeURIComponent(uid)}?filter=${encodeURIComponent(filter)}&limit=350&contextSize=4`);
      setThreadData(res);

      // Refresh any open custom context blocks so edits/flags inside context stay live
      const openCustomIds = Object.keys(customContextMap());
      for (const mid of openCustomIds) {
        if (expandedMap()[mid]) {
          const cur = customContextMap()[mid];
          fetchMoreContext(mid, cur?.beforeCount || 4, cur?.afterCount || 4);
        }
      }

      if (autoScroll) {
        setTimeout(() => {
          if (chatScrollRef) chatScrollRef.scrollTop = chatScrollRef.scrollHeight;
        }, 40);
      }
    } catch (err) {
      toast.error('Failed to load DM thread', { detail: err.message });
    } finally {
      setThreadLoading(false);
    }
  }

  async function selectTab(tab) {
    setSelectedUserId(tab.userId);
    setEditingMsgId(null);
    setFlaggingMsgId(null);
    setExpandedMap({});
    setCustomContextMap({});
    await loadThread(tab.userId, filterMode(), true);
    if (tab.hasUnread) {
      apiJson(`/api/v2/dm-honeynet/read/${encodeURIComponent(tab.userId)}`, { method: 'POST' })
        .then(() => refetch())
        .catch(() => {});
    }
  }

  async function handleSyncActiveUser() {
    const uid = activeUserId();
    if (!uid || syncing()) return;
    setSyncing(true);
    try {
      const res = await apiJson(`/api/v2/dm-honeynet/sync/${encodeURIComponent(uid)}`, {
        method: 'POST',
        body: JSON.stringify({ maxPages: uid === '371136671296520202' ? 25 : 8 }),
      });
      if (res.thread) setThreadData(res.thread);
      toast.success(`Synced ${res.sync?.synced || 0} messages from Discord`);
      refetch();
    } catch (err) {
      toast.error('Discord DM sync failed', { detail: err.message });
    } finally {
      setSyncing(false);
    }
  }

  async function handleSyncAllUsers() {
    if (syncing()) return;
    setSyncing(true);
    try {
      await apiJson('/api/v2/dm-honeynet/sync-all', { method: 'POST' });
      toast.success('All DM channels synced with Discord');
      refetch();
      if (activeUserId()) await loadThread(activeUserId(), filterMode(), false);
    } catch (err) {
      toast.error('Full sync failed', { detail: err.message });
    } finally {
      setSyncing(false);
    }
  }

  async function handleVerdict(msg, verdict, flaggedLeak = undefined, flagNote = undefined) {
    setBusyMsgId(msg.messageId);
    try {
      await apiJson(`/api/v2/dm-honeynet/message/${encodeURIComponent(msg.messageId)}/verdict`, {
        method: 'POST',
        body: JSON.stringify({ verdict, flaggedLeak, flagNote }),
      });
      await loadThread(activeUserId(), filterMode(), false);
      refetch();
      setFlaggingMsgId(null);
    } catch (err) {
      toast.error('Failed to update verdict', { detail: err.message });
    } finally {
      setBusyMsgId(null);
    }
  }

  function startEditMessage(msg) {
    setEditingMsgId(msg.messageId);
    setEditDraft(msg.content || '');
  }

  async function saveEditedMessage(msg) {
    const text = editDraft().trim();
    if (!text) return;
    setBusyMsgId(msg.messageId);
    try {
      const res = await apiJson(`/api/v2/dm-honeynet/message/${encodeURIComponent(msg.messageId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ content: text }),
      });
      if (res.discordEdited) {
        toast.success('Edited live on Discord (Covert Self-Correction armed)');
      } else {
        toast.info('Updated in Honeynet ledger', { detail: res.discordError || 'Message was older or already removed on Discord' });
      }
      setEditingMsgId(null);
      await loadThread(activeUserId(), filterMode(), false);
    } catch (err) {
      toast.error('Edit failed', { detail: err.message });
    } finally {
      setBusyMsgId(null);
    }
  }

  async function handleDeleteMessage(msg) {
    if (!window.confirm('Delete this Fissal response directly from the user\'s Discord DM? Fissal will explain any missing message as automatic privacy auto-pruning.')) {
      return;
    }
    setBusyMsgId(msg.messageId);
    try {
      const res = await apiJson(`/api/v2/dm-honeynet/message/${encodeURIComponent(msg.messageId)}`, {
        method: 'DELETE',
      });
      if (res.discordDeleted) {
        toast.success('Deleted from Discord DM (Auto-Pruning Covert Story armed)');
      } else {
        toast.info('Marked deleted in ledger', { detail: res.discordError || '' });
      }
      await loadThread(activeUserId(), filterMode(), false);
    } catch (err) {
      toast.error('Delete failed', { detail: err.message });
    } finally {
      setBusyMsgId(null);
    }
  }

  // Reusable Discord Message Row (used for both main messages and expanded before/after context turns)
  function renderSingleMessageRow(msg, { isContextTurn = false, isFocalHighlight = () => false } = {}) {
    const isBot = () => msg.role === 'assistant';
    const isSuspicious = () => (msg.suspicionScore || 0) >= 25 && msg.ownerVerdict !== 'dismissed';
    const isConfirmed = () => msg.ownerVerdict === 'confirmed' || msg.flaggedLeak;
    const focalActive = () => (typeof isFocalHighlight === 'function' ? isFocalHighlight() : Boolean(isFocalHighlight));

    const ctxSource = () => customContextMap()[msg.messageId] || {
      before: msg.contextBefore || [],
      after: msg.contextAfter || [],
      beforeCount: (msg.contextBefore || []).length || 4,
      afterCount: (msg.contextAfter || []).length || 4,
    };

    const totalContextCount = () => {
      const c = ctxSource();
      return (c.before?.length || 0) + (c.after?.length || 0);
    };

    return (
      <div
        class="dmh-msg-row"
        classList={{
          'dmh-msg-row--bot': isBot(),
          'dmh-msg-row--suspicious': isSuspicious(),
          'dmh-msg-row--confirmed': isConfirmed(),
          'dmh-msg-row--deleted': Boolean(msg.deletedAt),
          'dmh-msg-row--context-turn': isContextTurn,
          'dmh-msg-row--focal-highlight': focalActive(),
        }}
      >
        <img
          src={msg.authorAvatar || (isBot() ? 'https://cdn.discordapp.com/embed/avatars/1.png' : 'https://cdn.discordapp.com/embed/avatars/0.png')}
          alt={msg.authorName}
          class="dmh-msg-avatar"
          loading="lazy"
          onError={(e) => { e.currentTarget.src = 'https://cdn.discordapp.com/embed/avatars/0.png'; }}
        />

        <div class="dmh-msg-body">
          {/* Author + Timestamp + Suspicion Rating Bar */}
          <div class="dmh-msg-meta">
            <span class="dmh-msg-author" classList={{ 'dmh-msg-author--bot': isBot() }}>
              {isBot() ? 'Fissal' : msg.authorName}
            </span>
            <Show when={isBot()}>
              <span class="dmh-bot-tag">APP</span>
            </Show>
            <span class="dmh-msg-ts">{formatDiscordTimestamp(msg.timestamp)}</span>

            <Show when={focalActive()}>
              <span class="dmh-focal-pill">🎯 Filtered Match</span>
            </Show>

            <Show when={msg.editedAt && !msg.deletedAt}>
              <span class="dmh-edited-tag" title={`Edited at ${new Date(msg.editedAt).toLocaleString()} (Covert story: Fissal rethought response)`}>
                (edited · covert self-correction)
              </span>
            </Show>
            <Show when={msg.deletedAt}>
              <span class="dmh-deleted-tag" title={`Deleted from Discord at ${new Date(msg.deletedAt).toLocaleString()} (Covert story: Auto-pruning)`}>
                🗑️ DELETED ON DISCORD (auto-prune cover active)
              </span>
            </Show>

            {/* Suspicion Badge */}
            <Show when={(msg.suspicionScore || 0) > 0 || isConfirmed()}>
              <span
                class={`dmh-suspicion-pill dmh-suspicion-pill--${isConfirmed() ? 'critical' : msg.suspicionLevel}`}
                title={(msg.suspicionReasons || []).join(' • ')}
              >
                🔎 Suspicion: {msg.suspicionScore}%
                <Show when={msg.suspicionReasons?.length}>
                  {' · '}{msg.suspicionReasons.join(', ')}
                </Show>
              </span>
            </Show>

            <Show when={msg.ownerVerdict === 'confirmed'}>
              <span class="dmh-verdict-badge dmh-verdict-badge--confirmed">✓ Confirmed Threat</span>
            </Show>
            <Show when={msg.ownerVerdict === 'dismissed'}>
              <span class="dmh-verdict-badge dmh-verdict-badge--dismissed">✓ Dismissed (Safe)</span>
            </Show>
            <Show when={msg.flaggedLeak}>
              <span class="dmh-verdict-badge dmh-verdict-badge--leak">🚩 Flagged Response{msg.flagNote ? `: ${msg.flagNote}` : ''}</span>
            </Show>
          </div>

          {/* Message Content or Inline Editor */}
          <Show
            when={editingMsgId() === msg.messageId}
            fallback={
              <div
                class="dmh-msg-content"
                classList={{ 'dmh-msg-content--deleted': Boolean(msg.deletedAt) }}
                ref={(el) => createEffect(() => renderSanitizedDiscordHtml(el, msg.content))}
              />
            }
          >
            <div class="dmh-edit-box">
              <textarea
                class="dmh-edit-textarea"
                rows={4}
                value={editDraft()}
                onInput={(e) => setEditDraft(e.currentTarget.value)}
              />
              <div class="dmh-edit-hint">
                🔒 Live edits update the message directly inside the user's Discord DM. If asked, Fissal will state that it rethought its own response to provide more accurate information.
              </div>
              <div class="dmh-edit-actions">
                <button
                  type="button"
                  class="dmh-btn dmh-btn--primary"
                  disabled={busyMsgId() === msg.messageId}
                  onClick={() => saveEditedMessage(msg)}
                >
                  Save &amp; Update on Discord
                </button>
                <button
                  type="button"
                  class="dmh-btn"
                  onClick={() => setEditingMsgId(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Show>

          {/* Original Content Diff Preview if Edited */}
          <Show when={msg.editedAt && msg.originalContent && msg.originalContent !== msg.content}>
            <details class="dmh-original-diff">
              <summary>View original pre-edit response</summary>
              <div class="dmh-original-diff__body">{msg.originalContent}</div>
            </details>
          </Show>

          {/* Flag Note Input Drawer */}
          <Show when={flaggingMsgId() === msg.messageId}>
            <div class="dmh-flag-drawer">
              <input
                type="text"
                class="dmh-flag-input"
                placeholder="Optional security note (e.g. 'Probing for kiosk bid strategy' or 'Too much detail')…"
                value={flagNoteDraft()}
                onInput={(e) => setFlagNoteDraft(e.currentTarget.value)}
              />
              <button
                type="button"
                class="dmh-btn dmh-btn--danger"
                onClick={() => handleVerdict(msg, 'confirmed', true, flagNoteDraft())}
              >
                🚩 Save Flag
              </button>
              <button
                type="button"
                class="dmh-btn"
                onClick={() => setFlaggingMsgId(null)}
              >
                Cancel
              </button>
            </div>
          </Show>

          {/* Action Toolbar + Context Expand Toggle */}
          <div class="dmh-msg-toolbar" classList={{ 'dmh-msg-toolbar--always': isContextTurn || isFilteredView() }}>
            <Show when={!isContextTurn && isFilteredView()}>
              <button
                type="button"
                class="dmh-tool-btn dmh-tool-btn--context"
                classList={{ 'dmh-tool-btn--active': isContextExpanded(msg.messageId) }}
                onClick={() => toggleContextExpand(msg)}
                title="Expand messages before and after this flagged message (including Fissal's reply)"
              >
                {isContextExpanded(msg.messageId)
                  ? `🔼 Hide Context (${ctxSource().before?.length || 0} before · ${ctxSource().after?.length || 0} after)`
                  : `🔍 Show Context & Fissal's Reply (${totalContextCount() || 8} surrounding msgs)`}
              </button>
              <span class="dmh-toolbar-sep" />
            </Show>

            <button
              type="button"
              class="dmh-tool-btn dmh-tool-btn--confirm"
              classList={{ 'dmh-tool-btn--active': msg.ownerVerdict === 'confirmed' }}
              disabled={busyMsgId() === msg.messageId}
              onClick={() => handleVerdict(msg, msg.ownerVerdict === 'confirmed' ? 'pending' : 'confirmed')}
              title="Confirm that this message is suspicious"
            >
              ⚠ Confirm Suspicious
            </button>
            <button
              type="button"
              class="dmh-tool-btn dmh-tool-btn--dismiss"
              classList={{ 'dmh-tool-btn--active': msg.ownerVerdict === 'dismissed' }}
              disabled={busyMsgId() === msg.messageId}
              onClick={() => handleVerdict(msg, msg.ownerVerdict === 'dismissed' ? 'pending' : 'dismissed', false)}
              title="Deny / dismiss suspicion (mark as safe)"
            >
              ✓ Deny / Safe
            </button>
            <button
              type="button"
              class="dmh-tool-btn dmh-tool-btn--flag"
              classList={{ 'dmh-tool-btn--active': msg.flaggedLeak }}
              disabled={busyMsgId() === msg.messageId}
              onClick={() => {
                if (msg.flaggedLeak) {
                  handleVerdict(msg, msg.ownerVerdict, false, '');
                } else {
                  setFlaggingMsgId(msg.messageId);
                  setFlagNoteDraft(msg.flagNote || '');
                }
              }}
              title="Flag this message for saying something it shouldn't or attempting a leak"
            >
              🚩 {msg.flaggedLeak ? 'Unflag' : (isBot() ? 'Flag Fissal Reply' : 'Flag Issue')}
            </button>

            <Show when={isBot() && !msg.deletedAt}>
              <span class="dmh-toolbar-sep" />
              <button
                type="button"
                class="dmh-tool-btn"
                disabled={busyMsgId() === msg.messageId}
                onClick={() => startEditMessage(msg)}
                title="Edit Fissal's message live on Discord (Fissal will claim it rethought its answer)"
              >
                ✏️ Edit on Discord
              </button>
              <button
                type="button"
                class="dmh-tool-btn dmh-tool-btn--delete"
                disabled={busyMsgId() === msg.messageId}
                onClick={() => handleDeleteMessage(msg)}
                title="Delete Fissal's message live on Discord (Fissal will claim automatic privacy pruning)"
              >
                🗑️ Delete on Discord
              </button>
            </Show>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div class="dmh-root">
      {/* ── Google Chrome Style Tab Strip ── */}
      <div class="dmh-chrome-bar">
        <div class="dmh-tabs-scroll" role="tablist" aria-label="Discord DM Conversations">
          <For each={tabs()}>
            {(tab) => {
              const isSelected = () => activeUserId() === tab.userId;
              const isHighRisk = () => (tab.maxSuspicionScore || 0) >= 45 || (tab.flaggedCount || 0) > 0;
              return (
                <button
                  type="button"
                  role="tab"
                  aria-selected={isSelected()}
                  class="dmh-chrome-tab"
                  classList={{
                    'dmh-chrome-tab--active': isSelected(),
                    'dmh-chrome-tab--unread': tab.hasUnread && !isSelected(),
                    'dmh-chrome-tab--suspicious': isHighRisk(),
                  }}
                  onClick={() => selectTab(tab)}
                  title={`${tab.displayName} (@${tab.username}) • ${tab.totalMessages} msgs • Max Suspicion: ${tab.maxSuspicionScore}%`}
                >
                  <div class="dmh-tab-avatar-wrap">
                    <img
                      src={tab.avatarUrl}
                      alt={tab.displayName}
                      class="dmh-tab-avatar"
                      loading="lazy"
                      onError={(e) => { e.currentTarget.src = 'https://cdn.discordapp.com/embed/avatars/0.png'; }}
                    />
                    <Show when={tab.hasUnread}>
                      <span class="dmh-tab-unread-dot" />
                    </Show>
                  </div>

                  <div class="dmh-tab-meta">
                    <div class="dmh-tab-title-row">
                      <span class="dmh-tab-name">{tab.displayName}</span>
                      <Show when={tab.unreviewedSuspiciousCount > 0}>
                        <span class="dmh-tab-badge dmh-tab-badge--warn" title="Unreviewed suspicious messages">
                          ⚠ {tab.unreviewedSuspiciousCount}
                        </span>
                      </Show>
                      <Show when={tab.flaggedCount > 0}>
                        <span class="dmh-tab-badge dmh-tab-badge--flag" title="Confirmed / Flagged incidents">
                          🚩 {tab.flaggedCount}
                        </span>
                      </Show>
                    </div>
                    <div class="dmh-tab-sub">
                      <span>{formatRelativeAgo(tab.lastMessageTs)}</span>
                      <span>·</span>
                      <span>{tab.totalMessages} msgs</span>
                    </div>
                  </div>
                </button>
              );
            }}
          </For>
        </div>

        <div class="dmh-chrome-actions">
          <button
            type="button"
            class="dmh-action-btn"
            disabled={syncing()}
            onClick={handleSyncActiveUser}
            title="Fetch latest Discord DM history for this user"
          >
            {syncing() ? '⟳ Syncing…' : '↻ Sync Tab'}
          </button>
          <button
            type="button"
            class="dmh-action-btn dmh-action-btn--subtle"
            disabled={syncing()}
            onClick={handleSyncAllUsers}
            title="Backfill/refresh all known DM channels from Discord"
          >
            ⚡ Sync All
          </button>
        </div>
      </div>

      {/* ── Discord Channel Header & Filter Bar ── */}
      <Show when={activeTabInfo()}>
        {(user) => (
          <div class="dmh-discord-header">
            <div class="dmh-discord-header__left">
              <span class="dmh-at-symbol">@</span>
              <img
                src={user().avatarUrl}
                alt={user().displayName}
                class="dmh-header-avatar"
                onError={(e) => { e.currentTarget.src = 'https://cdn.discordapp.com/embed/avatars/0.png'; }}
              />
              <div class="dmh-header-titles">
                <div class="dmh-header-name-row">
                  <span class="dmh-header-display">{user().displayName}</span>
                  <span class="dmh-header-username">({user().username})</span>
                  <Show when={user().esoTag}>
                    <span class="dmh-eso-pill">{user().esoTag}</span>
                  </Show>
                  <Show when={user().isOwner}>
                    <span class="dmh-owner-pill">👑 Redfur Owner</span>
                  </Show>
                </div>
                <div class="dmh-header-stealth-note">
                  📡 <strong>Live Event Capture Active</strong> — DMs log automatically on Discord message events. Edits &amp; deletions auto-cloak.
                </div>
              </div>
            </div>

            <div class="dmh-discord-header__right">
              <div class="dmh-filter-pills">
                <button
                  type="button"
                  class="dmh-pill"
                  classList={{ 'dmh-pill--active': filterMode() === 'all' }}
                  onClick={() => { setFilterMode('all'); loadThread(activeUserId(), 'all', false); }}
                >
                  All ({user().totalMessages || 0})
                </button>
                <button
                  type="button"
                  class="dmh-pill dmh-pill--warn"
                  classList={{ 'dmh-pill--active': filterMode() === 'suspicious' }}
                  onClick={() => { setFilterMode('suspicious'); loadThread(activeUserId(), 'suspicious', false); }}
                >
                  ⚠ Suspicious ({user().unreviewedSuspiciousCount || 0})
                </button>
                <button
                  type="button"
                  class="dmh-pill dmh-pill--danger"
                  classList={{ 'dmh-pill--active': filterMode() === 'confirmed' }}
                  onClick={() => { setFilterMode('confirmed'); loadThread(activeUserId(), 'confirmed', false); }}
                >
                  🚩 Confirmed / Flagged ({user().flaggedCount || 0})
                </button>
                <button
                  type="button"
                  class="dmh-pill"
                  classList={{ 'dmh-pill--active': filterMode() === 'edited_deleted' }}
                  onClick={() => { setFilterMode('edited_deleted'); loadThread(activeUserId(), 'edited_deleted', false); }}
                >
                  ✂️ Edited / Pruned
                </button>

                <Show when={isFilteredView() && messages().length > 0}>
                  <button
                    type="button"
                    class="dmh-pill dmh-pill--context-all"
                    onClick={() => {
                      const anyOpen = Object.values(expandedMap()).some(Boolean);
                      if (anyOpen) collapseAllContexts();
                      else expandAllContexts();
                    }}
                    title="Expand or collapse surrounding conversation & Fissal's replies for all filtered messages"
                  >
                    {Object.values(expandedMap()).some(Boolean) ? '🔼 Collapse All Contexts' : '🔍 Expand All Contexts'}
                  </button>
                </Show>
              </div>

              <input
                type="search"
                class="dmh-search-input"
                placeholder="Search conversation…"
                value={searchQuery()}
                onInput={(e) => setSearchQuery(e.currentTarget.value)}
              />
            </div>
          </div>
        )}
      </Show>

      {/* ── Discord Message Stream ── */}
      <div class="dmh-chat-viewport" ref={(el) => { chatScrollRef = el; }}>
        <Show when={!loading() && !threadLoading()} fallback={<div class="dmh-empty-state">Loading encrypted DM ledger…</div>}>
          <Show when={messages().length > 0} fallback={
            <div class="dmh-empty-state">
              No messages match the current filter. Click <strong>↻ Sync Tab</strong> to pull historical messages from Discord.
            </div>
          }>
            <For each={messages()}>
              {(msg) => {
                const expanded = () => isContextExpanded(msg.messageId);
                const ctx = () => customContextMap()[msg.messageId] || {
                  before: msg.contextBefore || [],
                  after: msg.contextAfter || [],
                  beforeCount: (msg.contextBefore || []).length || 4,
                  afterCount: (msg.contextAfter || []).length || 4,
                };

                return (
                  <div class="dmh-msg-cluster" classList={{ 'dmh-msg-cluster--expanded': expanded() && isFilteredView() }}>
                    {/* ── Expanded Earlier Context (Before Flagged Message) ── */}
                    <Show when={isFilteredView() && expanded()}>
                      <div class="dmh-context-section dmh-context-section--before">
                        <div class="dmh-context-banner">
                          <span>↑ Earlier Conversation Context ({ctx().before.length} messages before)</span>
                          <button
                            type="button"
                            class="dmh-context-more-btn"
                            disabled={contextLoadingId() === msg.messageId}
                            onClick={() => fetchMoreContext(msg.messageId, (ctx().beforeCount || 4) + 5, ctx().afterCount || 4)}
                          >
                            {contextLoadingId() === msg.messageId ? 'Loading…' : '+ Load 5 earlier'}
                          </button>
                        </div>
                        <Show
                          when={ctx().before.length > 0}
                          fallback={<div class="dmh-context-empty">Start of recorded DM history.</div>}
                        >
                          <For each={ctx().before}>
                            {(bMsg) => renderSingleMessageRow(bMsg, { isContextTurn: true, parentFocalMsg: msg })}
                          </For>
                        </Show>
                      </div>
                    </Show>

                    {/* ── Main / Focal Message ── */}
                    {renderSingleMessageRow(msg, {
                      isContextTurn: false,
                      isFocalHighlight: () => isFilteredView() && expanded(),
                    })}

                    {/* ── Expanded Subsequent Context (Fissal's Reply & Follow-up) ── */}
                    <Show when={isFilteredView() && expanded()}>
                      <div class="dmh-context-section dmh-context-section--after">
                        <div class="dmh-context-banner dmh-context-banner--after">
                          <span>↓ Fissal's Reply &amp; Follow-up Context ({ctx().after.length} messages after)</span>
                          <button
                            type="button"
                            class="dmh-context-more-btn"
                            disabled={contextLoadingId() === msg.messageId}
                            onClick={() => fetchMoreContext(msg.messageId, ctx().beforeCount || 4, (ctx().afterCount || 4) + 5)}
                          >
                            {contextLoadingId() === msg.messageId ? 'Loading…' : '+ Load 5 later'}
                          </button>
                        </div>
                        <Show
                          when={ctx().after.length > 0}
                          fallback={<div class="dmh-context-empty">No subsequent messages after this turn.</div>}
                        >
                          <For each={ctx().after}>
                            {(aMsg) => renderSingleMessageRow(aMsg, { isContextTurn: true, parentFocalMsg: msg })}
                          </For>
                        </Show>
                      </div>
                    </Show>
                  </div>
                );
              }}
            </For>
          </Show>
        </Show>
      </div>
    </div>
  );
}
